import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { CiSearch } from "react-icons/ci";
import {
  MdCheckCircle,
  MdCancel,
  MdImage,
  MdVisibility,
  MdChat,
} from "react-icons/md";
import toast from "react-hot-toast";
import moment from "moment";
import Loader from "../../components/Loader";
import NoData from "../../components/NoData";
import DepositServices, {
  depositScreenshotUrl,
} from "../../services/DepositServices";
import { AuthContext } from "../../context/AuthContext";
import QuickChatModal from "../../components/QuickChatModal";
import DepositActionModal from "./modals/DepositActionModal";
import DepositDetailModal from "./modals/DepositDetailModal";
import ExportMenu from "../../components/ExportMenu";
import "../../styles/deposit.css";

const STATUS_TABS = [
  { key: "", label: "All" },
  { key: "PENDING", label: "Pending" },
  { key: "APPROVED", label: "Approved" },
  { key: "REJECTED", label: "Rejected" },
];

const PAGE_SIZES = [10, 25, 50, 100];

const money = (v) => Number(v || 0).toFixed(2);

/** Shared by the Excel and PDF export so the two can never drift apart. */
const EXPORT_COLUMNS = [
  { header: "Sr.", value: (r, i) => i + 1 },
  { header: "Player", value: (r) => r.user_name || "-" },
  { header: "Game ID", value: (r) => r.game_id || "-" },
  { header: "Mobile", value: (r) => r.mobile || "-" },
  { header: "Amount", value: (r) => Number(r.amount || 0) },
  { header: "Bonus", value: (r) => Number(r.discount_amount || 0) },
  { header: "Bonus %", value: (r) => Number(r.discount_percentage || 0) },
  { header: "Credited", value: (r) => Number(r.credited_amount || 0) },
  { header: "Method", value: (r) => r.method || "-" },
  { header: "Account", value: (r) => r.account_name || "-" },
  { header: "UTR", value: (r) => r.utr_number || "Not submitted" },
  { header: "Status", value: (r) => r.status },
  {
    header: "Requested",
    value: (r) =>
      r.created_at ? moment(r.created_at).format("DD/MM/YYYY hh:mm A") : "-",
  },
  {
    header: "Processed",
    value: (r) =>
      r.processed_at ? moment(r.processed_at).format("DD/MM/YYYY hh:mm A") : "-",
  },
  { header: "Processed By", value: (r) => r.processed_by_name || "-" },
  { header: "Remark", value: (r) => r.admin_remark || "-" },
];

const DepositManagement = () => {
  const depositApi = new DepositServices();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [status, setStatus] = useState("");
  const [searchFilter, setSearchFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [actionModal, setActionModal] = useState(null); // {row, mode}
  const [chatRow, setChatRow] = useState(null); // pending row being messaged
  const [detailRow, setDetailRow] = useState(null);
  const [proofUrl, setProofUrl] = useState(null);

  const getList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await depositApi.getDepositList(
        page,
        limit,
        status,
        searchFilter,
        fromDate,
        toDate,
      );
      if (response.isSuccess) {
        setRows(response.data || []);
        setPagination(response.pagination || null);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Failed to load deposit requests");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, status, searchFilter, fromDate, toDate]);

  useEffect(() => {
    getList();
  }, [getList]);

  // getList is rebuilt on every filter change; keeping it in a ref lets the
  // socket effect below call the latest version without reconnecting each time.
  const getListRef = useRef(getList);
  useEffect(() => {
    getListRef.current = getList;
  }, [getList]);

  /**
   * Live refresh while the page is open. TopBar owns the socket connection and
   * re-broadcasts new requests as a window event, so this listens locally
   * rather than opening a second socket - which would also mean two toasts for
   * the same deposit. The toast stays with TopBar; this only reloads the list.
   */
  useEffect(() => {
    const onNewDeposit = () => getListRef.current();
    window.addEventListener("yantra:newDepositRequest", onNewDeposit);
    return () =>
      window.removeEventListener("yantra:newDepositRequest", onNewDeposit);
  }, []);

  /**
   * The proof endpoint is auth-scoped, so it cannot be used directly as an
   * <img src> - fetch it with the token and hand the blob to the viewer.
   */
  const openProof = async (filename) => {
    try {
      const res = await fetch(depositScreenshotUrl(filename), {
        headers: {
          Authorization: `Bearer ${window.localStorage.getItem("token")}`,
        },
      });
      if (!res.ok) {
        toast.error("Could not load payment screenshot");
        return;
      }
      const blob = await res.blob();
      setProofUrl(URL.createObjectURL(blob));
    } catch (error) {
      toast.error("Could not load payment screenshot");
    }
  };

  const closeProof = () => {
    if (proofUrl) URL.revokeObjectURL(proofUrl);
    setProofUrl(null);
  };

  const statusBadge = (s) => (
    <span className={`dp-badge dp-badge-${String(s).toLowerCase()}`}>{s}</span>
  );

  return (
    <div className="deposit-page">
      <div className="dp-header">
        <div>
          <h2 className="dp-title">Deposit Requests</h2>
          <p className="dp-subtitle">
            {Number(user?.role_id) === 1
              ? "All deposit requests across the platform."
              : "Deposit requests from your mobile users."}{" "}
            Approving debits your wallet and credits the player.
          </p>
        </div>
      </div>

      <div className="dp-filters">
        <div className="dp-tabs">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key || "ALL"}
              className={`dp-tab ${status === tab.key ? "active" : ""}`}
              onClick={() => {
                setStatus(tab.key);
                setPage(1);
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <div className="dp-search">
          <CiSearch size={18} />
          <input
            type="text"
            placeholder="Search user, game ID, mobile or UTR"
            value={searchFilter}
            onChange={(e) => {
              setSearchFilter(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className="dp-dates">
          <label>From</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => {
              setFromDate(e.target.value);
              setPage(1);
            }}
          />
          <label>To</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => {
              setToDate(e.target.value);
              setPage(1);
            }}
          />
          {(fromDate || toDate) && (
            <button
              className="dp-clear"
              onClick={() => {
                setFromDate("");
                setToDate("");
                setPage(1);
              }}
            >
              Clear
            </button>
          )}
        </div>

        <div className="page-size">
          <span>Show</span>
          <select
            value={limit}
            onChange={(e) => {
              setLimit(Number(e.target.value));
              setPage(1);
            }}
          >
            {PAGE_SIZES.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>

        {/* Exports the whole filtered range, not just the page on screen. */}
        <ExportMenu
          fetchAll={() =>
            depositApi.getDepositListForExport({
              status,
              filter: searchFilter,
              fromDate,
              toDate,
            })
          }
          columns={EXPORT_COLUMNS}
          fileName={`deposit_requests_${fromDate || "all"}_to_${toDate || "all"}`}
          title="Deposit Requests"
          subtitle={
            fromDate || toDate
              ? `From ${fromDate || "start"} to ${toDate || "today"}${status ? ` | ${status}` : ""}`
              : status
                ? `Status: ${status}`
                : "All deposit requests"
          }
          disabled={loading}
        />
      </div>

      {loading ? (
        <Loader message="Loading deposit requests..." />
      ) : rows.length === 0 ? (
        <NoData
          message="No Deposit Requests"
          subText={
            status === "PENDING"
              ? "Nothing waiting for approval right now"
              : "Try a different status or search"
          }
        />
      ) : (
        <div className="dp-table-wrapper">
          <table className="dp-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Player</th>
                <th>Amount</th>
                <th>Bonus</th>
                <th>Credited</th>
                <th>Method</th>
                <th>UTR</th>
                <th>Proof</th>
                <th>Requested</th>
                <th>Status</th>
                <th className="text-end dp-col-action">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.id}>
                  <td>{(page - 1) * limit + index + 1}</td>
                  <td>
                    <div className="dp-user">{row.user_name}</div>
                    <small className="dp-muted">
                      {row.game_id} · {row.mobile}
                    </small>
                  </td>
                  <td className="dp-amount">₹{money(row.amount)}</td>
                  <td>
                    {Number(row.discount_amount) > 0 ? (
                      <span className="dp-bonus">
                        +₹{money(row.discount_amount)}
                        <small> ({Number(row.discount_percentage)}%)</small>
                      </span>
                    ) : (
                      <span className="dp-muted">—</span>
                    )}
                  </td>
                  <td className="dp-credited">₹{money(row.credited_amount)}</td>
                  <td>
                    <span className="dp-method">{row.method || "—"}</span>
                    <br />
                    <small className="dp-muted">{row.account_name}</small>
                  </td>
                  <td className="dp-utr">
                    {row.utr_number || (
                      <span className="dp-no-utr" title="Player has not submitted a UTR">
                        Not submitted
                      </span>
                    )}
                  </td>
                  <td>
                    {row.screenshot ? (
                      <button
                        className="btn btn-link p-0 dp-proof"
                        onClick={() => openProof(row.screenshot)}
                      >
                        <MdImage size={16} /> View
                      </button>
                    ) : (
                      <span className="dp-muted">—</span>
                    )}
                  </td>
                  <td className="dp-muted">
                    {moment(row.created_at).format("DD MMM YY, hh:mm A")}
                  </td>
                  <td>
                    {statusBadge(row.status)}
                    {row.admin_remark && (
                      <div className="dp-remark" title={row.admin_remark}>
                        {row.admin_remark}
                      </div>
                    )}
                  </td>
                  <td className="text-end dp-col-action">
                    <div className="dp-actions">
                      {row.status !== "PENDING" && (
                        <span
                          className="dp-muted dp-processed-by"
                          title={`Processed by ${row.processed_by_name || "—"}`}
                        >
                          {row.processed_by_name || "—"}
                        </span>
                      )}
                      <button
                        className="dp-btn dp-btn-view"
                        title="View full details"
                        onClick={() => setDetailRow(row)}
                      >
                        <MdVisibility size={17} />
                      </button>
                      {row.status === "PENDING" && (
                        <>
                          <button
                            className="dp-btn dp-btn-chat"
                            title="Message this player"
                            onClick={() => setChatRow(row)}
                          >
                            <MdChat size={16} />
                          </button>
                          <button
                            className="dp-btn dp-btn-approve"
                            title={
                              row.utr_number
                                ? "Approve this deposit"
                                : "UTR not submitted — cannot approve"
                            }
                            disabled={!row.utr_number}
                            onClick={() =>
                              row.utr_number &&
                              setActionModal({ row, mode: "approve" })
                            }
                          >
                            <MdCheckCircle size={18} />
                          </button>
                          <button
                            className="dp-btn dp-btn-reject"
                            title="Reject this deposit"
                            onClick={() => setActionModal({ row, mode: "reject" })}
                          >
                            <MdCancel size={18} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pagination && pagination.totalPages > 1 && (
        <div className="dp-pagination">
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
            Previous
          </button>
          <span>
            Page {pagination.page} of {pagination.totalPages} ·{" "}
            {pagination.total} total
          </span>
          <button
            disabled={page >= pagination.totalPages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      )}

      {detailRow && (
        <DepositDetailModal
          row={detailRow}
          onClose={() => setDetailRow(null)}
          onAct={(row, mode) => {
            // Hand off to the confirm step so approving still requires a
            // deliberate second action.
            setDetailRow(null);
            setActionModal({ row, mode });
          }}
        />
      )}

      {chatRow && (
        <QuickChatModal
          player={{
            id: chatRow.user_id,
            name: chatRow.user_name,
            game_id: chatRow.game_id,
            mobile: chatRow.mobile,
          }}
          presets={[
            "Please share the UTR / reference number from your payment receipt.",
            "The UTR you sent does not match our bank statement. Please check and send it again.",
            "Please send a screenshot of the payment.",
          ]}
          onClose={() => setChatRow(null)}
        />
      )}

      {actionModal && (
        <DepositActionModal
          row={actionModal.row}
          mode={actionModal.mode}
          onClose={() => setActionModal(null)}
          onDone={() => {
            setActionModal(null);
            getList();
          }}
        />
      )}

      {proofUrl && (
        <div className="dp-proof-overlay" onClick={closeProof}>
          <img src={proofUrl} alt="Payment proof" />
        </div>
      )}
    </div>
  );
};

export default DepositManagement;
